/**
 * Reed.ai Design System — Checkbox
 *
 * States:   Default | Hover | Checked | Indeterminate
 * Sizes:    Desktop (24×24) | Mobile (24×24)
 *
 * Source: Figma "Reed.ai - Global components" — A / Checkbox page
 * Node:   1955:5877 (Checkbox/Desktop)
 *
 * Design details:
 *  Default  — white bg, 1px border #C2C6DB (piccadilly-blue-70), 4px radius
 *  Hover    — bg #C8C9CA (neutral-20), same border
 *  Checked  — bg #FFE5FA (carnaby-pink-30), border #F5C6EC (dark-carnaby-pink-70),
 *             white checkmark SVG centered in 16×16 area
 *  Label    — Red Hat Text Regular 16px piccadilly-blue (#081351)
 */

import React, { useId } from 'react';
import { cn } from '../../utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** Label text shown next to the checkbox */
  label?: string;
  /** Indeterminate state (partially checked) */
  indeterminate?: boolean;
  /** Desktop = 24px, Mobile = 24px (same size, label font changes) */
  size?: 'desktop' | 'mobile';
  className?: string;
  labelClassName?: string;
}

// ─── Checkmark SVG ────────────────────────────────────────────────────────────

function Checkmark() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="8"
      viewBox="0 0 12 8"
      width="12"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 4L4.5 7L11 1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function Dash() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="2"
      viewBox="0 0 10 2"
      width="10"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1 1h8" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Reed.ai Checkbox
 *
 * @example
 * // Uncontrolled
 * <Checkbox label="I agree to the terms" />
 *
 * // Controlled
 * const [checked, setChecked] = useState(false);
 * <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} label="Send me updates" />
 *
 * // Indeterminate
 * <Checkbox indeterminate label="Select all" />
 *
 * // Disabled
 * <Checkbox disabled label="Unavailable option" />
 */
export function Checkbox({
  label,
  indeterminate = false,
  size = 'desktop',
  className,
  labelClassName,
  id: idProp,
  checked,
  defaultChecked,
  disabled,
  onChange,
  ...rest
}: CheckboxProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;

  // Detect checked state for custom styling
  const isChecked = checked ?? false;
  const isIndeterminate = indeterminate && !isChecked;
  const showMark = isChecked || isIndeterminate;

  return (
    <label
      className={cn(
        'inline-flex items-center gap-4 cursor-pointer select-none group',
        disabled && 'opacity-30 cursor-not-allowed',
        className,
      )}
      htmlFor={id}
    >
      {/* Hidden native input */}
      <input
        {...rest}
        checked={checked}
        className="sr-only peer"
        defaultChecked={defaultChecked}
        disabled={disabled}
        id={id}
        type="checkbox"
        onChange={onChange}
        ref={(el) => {
          if (el) el.indeterminate = isIndeterminate;
        }}
      />

      {/* Custom checkbox visual */}
      <span
        className={cn(
          // Base
          'relative shrink-0 size-6 rounded-[4px] border transition-colors duration-150',
          'flex items-center justify-center',
          // Default
          'border-piccadilly-blue-70',
          // Hover (only when unchecked)
          !showMark && !disabled && 'group-hover:bg-neutral-20',
          // Checked state
          showMark && [
            'bg-carnaby-pink-30',
            'border-dark-carnaby-pink-70',
          ],
          // Peer focus (keyboard navigation)
          'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-dark-carnaby-pink peer-focus-visible:ring-offset-2',
        )}
        aria-hidden="true"
      >
        {isChecked && <span className="text-brand-dark-carnaby-pink"><Checkmark /></span>}
        {isIndeterminate && <span className="text-brand-dark-carnaby-pink"><Dash /></span>}
      </span>

      {/* Label */}
      {label && (
        <span
          className={cn(
            'font-body font-normal text-text-primary leading-[1.5]',
            size === 'desktop' ? 'text-body-md' : 'text-body-md-m',
            labelClassName,
          )}
        >
          {label}
        </span>
      )}
    </label>
  );
}

export default Checkbox;
